import React from 'react';
import type {
  TemplateBlock,
  BlockType,
  BlockSize,
} from 'services/NotificationTemplatesService';
import { renderWithSample } from 'services/NotificationTemplatesService';
import useStyles from './styles';

export const BLOCK_LABELS: Record<BlockType, string> = {
  header: 'Header',
  text: 'Texto',
  button: 'Botón',
  footer: 'Footer',
  image: 'Imagen',
  qr: 'QR',
};

export const isImageBlock = (type: BlockType): boolean =>
  type === 'header' || type === 'footer' || type === 'image';

const previewFontSize = (size?: BlockSize): number => {
  if (size === 'L') return 20;
  if (size === 'S') return 11;
  return 13;
};

interface TemplatePreviewProps {
  classes: ReturnType<typeof useStyles>;
  blocks: TemplateBlock[];
  brandColor: string;

  fillSample?: boolean;
}

const TemplatePreview = ({
  classes,
  blocks,
  brandColor,
  fillSample = false,
}: TemplatePreviewProps) => {

  if (!Array.isArray(blocks) || blocks.length === 0) return null;

  const renderRich = (raw: string): React.ReactNode => {
    const tokens = raw
      .split(/(<\/?(?:b|strong)>|<br\s*\/?>|<a\b[^>]*>|<\/a>|\{[a-z_]+\})/gi)
      .filter(Boolean);
    let bold = false;
    return tokens.map((tok, i) => {
      const lower = tok.toLowerCase();
      if (lower === '<b>' || lower === '<strong>') {
        bold = true;
        return null;
      }
      if (lower === '</b>' || lower === '</strong>') {
        bold = false;
        return null;
      }
      if (/^<br\s*\/?>$/i.test(tok)) {
        // eslint-disable-next-line react/no-array-index-key
        return <br key={i} />;
      }
      if (/^<a\b/i.test(tok) || lower === '</a>') return null;
      if (/^\{[a-z_]+\}$/.test(tok)) {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className={classes.var}>
            {tok}
          </span>
        );
      }
      return bold ? (
        // eslint-disable-next-line react/no-array-index-key
        <b key={i}>{tok}</b>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={i}>{tok}</React.Fragment>
      );
    });
  };

  const renderBlock = (block: TemplateBlock): React.ReactNode => {
    if (isImageBlock(block.type)) {
      if (!block.imageUrl) {
        return (
          <div
            key={block.id}
            style={{
              padding: 14,
              fontSize: 11,
              color: '#8c8c8c',
              textAlign: 'center',
              background: '#f5f5f5',
              borderBottom: '1px solid #eee',
            }}
          >
            {BLOCK_LABELS[block.type]} sin imagen
          </div>
        );
      }
      return (
        <img
          key={block.id}
          src={fillSample ? renderWithSample(block.imageUrl) : block.imageUrl}
          alt=""
          style={{ display: 'block', width: '100%', height: 'auto', border: 0 }}
        />
      );
    }
    if (block.type === 'qr') {
      const value = fillSample
        ? renderWithSample(block.content)
        : block.content;
      return (
        <div key={block.id} style={{ textAlign: 'center', padding: 14 }}>
          <div
            style={{
              width: 120,
              height: 120,
              margin: '0 auto',
              border: '2px solid #d9d9d9',
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fafafa',
              color: '#8c8c8c',
              fontSize: 11,
            }}
          >
            <span style={{ fontSize: 30, lineHeight: 1 }}>▦</span>
            <span>QR</span>
          </div>
          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 6 }}>
            Código: {value}
          </div>
        </div>
      );
    }

    if (block.type === 'button') {
      return (
        <div
          key={block.id}
          style={{ padding: '14px 18px', textAlign: 'center' }}
        >
          <span className={classes.emailBtn} style={{ background: brandColor }}>
            {block.content || 'Hacer seguimiento'}
          </span>
        </div>
      );
    }
    return (
      <p
        key={block.id}
        style={{
          fontFamily: 'sans-serif',
          fontSize: previewFontSize(block.size),
          lineHeight: 1.5,
          color: '#1f1f1f',
          textAlign: block.align ?? 'left',
          margin: 0,
          padding: '4px 18px',
        }}
      >
        {renderRich(
          fillSample ? renderWithSample(block.content) : block.content,
        )}
      </p>
    );
  };

  return <>{blocks.map((block) => renderBlock(block))}</>;
};

export default TemplatePreview;
